import { MdsIconKind } from "@/design-system/components/icon";
import { MdsIcon } from "@/design-system/components/icon";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { MDSButton, MDSListBox } from "@/mds";
import { MDSListBoxItemProps } from "@/mds/components/lists/list-box-item";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { toastModule } from "@/modules/toast";
import { uuidModule } from "@/modules/uuid";
import { IntegrationsApiPageContentForm } from "@/pages/integrations/api/integrations-api-page-content-form";
import { DeleteKeyModal } from "@/pages/integrations/api/integrations-api-page-delete-modal";
import {
  ApiKeyItemInfo,
  PendingApiKey,
} from "@/pages/integrations/api/integrations-api-page.types";
import { IntegrationsLayoutBottomBuffer } from "@/pages/integrations/shared/integrations-layout";
import { AppStore, useAppStore } from "@/store";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

type Props = {};

export const ApiKeyItemList: FC<Props> = observer(() => {
  const { store } = useAppStore();

  const [pendingApiKey, setPendingApiKey] = useState<PendingApiKey | null>(null);
  const [keyToDelete, setKeyToDelete] = useState<ApiKeyItemInfo | null>(null);
  const [formShown, setFormShown] = useState(false);
  const [nickname, setNickname] = useState("");

  const isLoading = store.apiKeys.isCreatingApiKey;

  const createApiKey = useCallback(async () => {
    const id = uuidModule.generate();
    const secretKeyToken = uuidModule.generate();

    await store.apiKeys.createApiKey({
      id,
      secretKeyToken,
      // Title and description are not set for now.
      title: null,
      description: null,
    });

    trackEvent(TrackedEvent.ApiKeyCreate, {
      api_key_id: id,
    });

    setPendingApiKey({ id, secretKeyToken });
  }, [store.apiKeys]);

  useEffect(() => {
    // When isCreatingApiKey becomes false and we have a pending key, show the modal
    if (!store.apiKeys.isCreatingApiKey && pendingApiKey) {
      setFormShown(true);
    }
  }, [store.apiKeys.isCreatingApiKey, pendingApiKey]);

  const onCreateHandler = useCallback(() => {
    if (isLoading) {
      return;
    }

    createApiKey();
  }, [createApiKey, isLoading]);

  const copyApiKey = useCallback(() => {
    if (pendingApiKey) {
      copyTextToClipboard({ text: `sk-mem-${pendingApiKey.secretKeyToken}` });
      toastModule.triggerToast({ content: "API key copied to clipboard" });
    }
  }, [pendingApiKey]);

  const deleteApiKey = useCallback((info: ApiKeyItemInfo) => {
    setKeyToDelete(info);
  }, []);

  const handleCloseForm = () => {
    setFormShown(false);
    setPendingApiKey(null);
    setNickname("");
  };

  const { items } = useItems({
    store,
    formShown,
    isLoading,
    pendingApiKey,
    onCreateHandler,
    deleteApiKey,
  });

  return (
    <Wrapper listHasItems={items.length > 0}>
      <MDSListBox label="Activated API keys" items={items} />
      <DeleteKeyModal keyToDelete={keyToDelete} setKeyToDelete={setKeyToDelete} />

      {formShown && (
        <IntegrationsApiPageContentForm
          nickname={nickname}
          pendingApiKey={pendingApiKey}
          copyApiKey={copyApiKey}
          handleCloseForm={handleCloseForm}
          setNickname={setNickname}
        />
      )}
      <IntegrationsLayoutBottomBuffer />
    </Wrapper>
  );
});

const useItems = ({
  store,
  formShown,
  isLoading,
  pendingApiKey,
  onCreateHandler,
  deleteApiKey,
}: {
  store: AppStore;
  formShown: boolean;
  isLoading: boolean;
  pendingApiKey: PendingApiKey | null;
  onCreateHandler: () => void;
  deleteApiKey: (info: ApiKeyItemInfo) => void;
}) => {
  const apiKeyInfoList: ApiKeyItemInfo[] = useMemo(() => {
    return store.apiKeys.allActiveApiKeys
      .filter(apiKey => apiKey.id !== pendingApiKey?.id)
      .map(apiKey => {
        return {
          id: apiKey.id,
          title: apiKey.title,
          createdAt: apiKey.createdAt,
          redactedToken: apiKey.redactedToken,
        };
      })
      .sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());
  }, [store.apiKeys.allActiveApiKeys, pendingApiKey]);

  const items: MDSListBoxItemProps[] = apiKeyInfoList.map(info => ({
    id: info.id,
    textValue: info.title + " " + info.redactedToken,
    leftAddon: <TitleText>{info.title}</TitleText>,
    children: <SecretTokenText>{info.redactedToken}</SecretTokenText>,
    rightAddon: (
      <MDSButton color="transparent" onPress={() => deleteApiKey(info)}>
        <MdsIcon kind={MdsIconKind.Trash} />
      </MDSButton>
    ),
  }));

  if (!formShown) {
    items.push({
      id: "create-api-key",
      textValue: "Create API key",
      onAction: onCreateHandler,
      isDisabled: isLoading,
      leftAddon: (
        <IconWrapper>
          <MdsIcon kind={MdsIconKind.Plus} />
        </IconWrapper>
      ),
      children: (
        <AddButtonText>{isLoading ? "Creating API key..." : "Add new API key"}</AddButtonText>
      ),
    });
  }

  return { items };
};

const Wrapper = styled.div<{ listHasItems: boolean }>(({ theme, listHasItems }) => ({
  width: "100%",
  marginTop: theme.spacing.sm,
  padding: 0,
  display: "flex",
  height: "100%",
  flexDirection: "column",
  gap: listHasItems ? theme.spacing.sm : theme.spacing.xs,
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const TitleText = styled.div(({ theme }) => ({
  width: "120px",
  height: 36,
  [theme.mediaQueries.rules.phone]: {
    width: "50px",
  },
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  display: "flex",
  alignItems: "center",
  fontWeight: theme.fontWeights.semiBold,
}));

const SecretTokenText = styled.div(() => ({
  fontFamily: "monaco, monospace",
}));

const IconWrapper = styled.div<{ variant?: "primary" | "secondary" }>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px",
  borderRadius: theme.borderRadius.maximum,

  "& svg": {
    height: 20,
    width: 20,
    color: theme.colors.grey.x400,
  },
}));

const AddButtonText = styled.div(({ theme }) => ({
  fontWeight: theme.fontWeights.medium,
  color: theme.colors.grey.x600,
}));
